import { createRouter, createWebHistory } from 'vue-router'
import {
  isAuthenticated,
  hasSessionObjectGuard,
  onlyAdminsGuard,
  onlyWorkshopGuard,
  onlyCustomerGuard,
  onlyInternalUsersGuard
} from './guards'
import { setupNavigationTracking } from '@/composables/useNavigationStack.js'

const routes = [
  {
    path: '/',
    name: 'home',
    component: () => import('@/components/ViewLanding.vue'),
    beforeEnter: [isAuthenticated],
  },
  {
    path: '/login',
    name: 'login',
    component: () => import('@/components/session/ViewLogin.vue'),
    props: true,
  },
  {
    path: '/logout',
    name: 'logout',
    component: () => import('@/components/session/ViewLogout.vue'),
  },


  // Management view (companies, locations, contacts, users)
  {
    path: '/admin',
    name: 'admin',
    component: () => import("@/components/admin/ViewAdmin.vue"),
    beforeEnter: [isAuthenticated, hasSessionObjectGuard, onlyAdminsGuard]
  },
  {
    path: "/account",
    name: "account",
    component: () => import("@/components/account/ViewAccount.vue"),
    beforeEnter: [isAuthenticated, hasSessionObjectGuard]
  },


  // RSO - Internal users view
  {
    path: "/rso",
    name: "rso",
    component: () => import("@/components/rso/ViewRSO.vue"),
    beforeEnter: [isAuthenticated, hasSessionObjectGuard, onlyInternalUsersGuard]
  },
  {
    path: "/rso/:uuid",
    name: "rso_detail",
    component: () => import("@/components/rso/ViewRSO.vue"),
    beforeEnter: [isAuthenticated, hasSessionObjectGuard, onlyInternalUsersGuard]
  },
  
  // Dashboard - External customer view
  {
    path: "/customer/dashboard",
    name: "customer_dashboard",
    component: () => import("@/components/external/customer/dashboard/ViewDashboard.vue"),
    beforeEnter: [isAuthenticated, hasSessionObjectGuard, onlyCustomerGuard]
  },
  
  // Account - External customer view
  {
    path: "/customer/account",
    name: "customer_account",
    component: () => import("@/components/external/customer/account/ViewAccountExternal.vue"),
    beforeEnter: [isAuthenticated, hasSessionObjectGuard, onlyCustomerGuard]
  },
  
  // RSO - External customer view
  {
    path: "/customer/rso",
    name: "customer_rso",
    component: () => import("@/components/external/customer/rso/ViewRSO.vue"),
    beforeEnter: [isAuthenticated, hasSessionObjectGuard, onlyCustomerGuard]
  },
  {
    path: "/customer/rso/:uuid",
    name: "customer_rso_detail",
    component: () => import("@/components/external/customer/rso/ViewRSO.vue"),
    beforeEnter: [isAuthenticated, hasSessionObjectGuard, onlyCustomerGuard]
  },
  {
    path: "/customer/rso/:uuid/detail",
    name: "customer_rso_detail_full",
    component: () => import("@/components/external/customer/rso/ViewOrderDetail.vue"),
    beforeEnter: [isAuthenticated, hasSessionObjectGuard, onlyCustomerGuard],
    props: true
  },

  // WO - Solo para usuarios internos y talleres externos
  {
    path: "/wo",
    name: "wo",
    component: () => import("@/components/wo/ViewWO.vue"),
    beforeEnter: [isAuthenticated, hasSessionObjectGuard, onlyWorkshopGuard]
  },
  {
    path: "/wo/:uuid",
    name: "wo_detail",
    component: () => import("@/components/wo/ViewWO.vue"),
    beforeEnter: [isAuthenticated, hasSessionObjectGuard, onlyWorkshopGuard]
  },

  // Shipments - Internal users
  {
    path: "/shipment",
    name: "shipment",
    component: () => import("@/components/shipment/ViewShipment.vue"),
    beforeEnter: [isAuthenticated, hasSessionObjectGuard, onlyInternalUsersGuard]
  },
  // Shipments - Warehouse intake view (WH Rotterdam) — must be before /:uuid
  {
    path: "/shipment/warehouse",
    name: "shipment_warehouse",
    component: () => import("@/components/shipment/ViewShipmentWarehouse.vue"),
    beforeEnter: [isAuthenticated, hasSessionObjectGuard, onlyInternalUsersGuard]
  },
  {
    path: "/shipment/:uuid",
    name: "shipment_detail",
    component: () => import("@/components/shipment/ViewShipment.vue"),
    beforeEnter: [isAuthenticated, hasSessionObjectGuard, onlyInternalUsersGuard]
  },
  
  // Components - External customer view
  {
    path: "/customer/components",
    name: "customer_components",
    component: () => import("@/components/external/customer/components/ViewComponents.vue"),
    beforeEnter: [isAuthenticated, hasSessionObjectGuard, onlyCustomerGuard]
  },
  
  // Analytics - Redirect to unified Dashboard
  {
    path: "/customer/analytics",
    name: "customer_analytics",
    redirect: { name: 'customer_dashboard' },
  },
  
  // Shipments - External users (customer)
  {
    path: "/customer/shipment",
    name: "customer_shipment",
    component: () => import("@/components/external/customer/shipment/ViewShipmentExternal.vue"),
    beforeEnter: [isAuthenticated, hasSessionObjectGuard, onlyCustomerGuard]
  },
  
  // Shipments - External users (workshop)
  {
    path: "/workshop/shipment",
    name: "workshop_shipment",
    component: () => import("@/components/external/customer/shipment/ViewShipmentExternal.vue"),
    beforeEnter: [isAuthenticated, hasSessionObjectGuard, onlyWorkshopGuard]
  },

  // Containers - Solo para usuarios internos
  {
    path: "/container",
    name: "container",
    component: () => import("@/components/container/ViewContainer.vue"),
    beforeEnter: [isAuthenticated, hasSessionObjectGuard, onlyInternalUsersGuard]
  },
  {
    path: "/container/:uuid",
    name: "container_detail",
    component: () => import("@/components/container/ViewContainer.vue"),
    beforeEnter: [isAuthenticated, hasSessionObjectGuard, onlyInternalUsersGuard]
  },

  // Partnumber - Solo para usuarios internos
  {
    path: "/partnumber",
    name: "partnumber",
    component: () => import("@/components/partnumber/ViewPartnumber.vue"),
    beforeEnter: [isAuthenticated, hasSessionObjectGuard, onlyInternalUsersGuard]
  },
  {
    path: "/partnumber/:uuid",
    name: "partnumber_detail",
    component: () => import("@/components/partnumber/ViewPartnumber.vue"),
    beforeEnter: [isAuthenticated, hasSessionObjectGuard, onlyInternalUsersGuard]
  },

  // CO2 Config - Solo para administradores
  {
    path: "/co2-config",
    name: "co2_config",
    component: () => import("@/components/co2/ViewCO2Config.vue"),
    beforeEnter: [isAuthenticated, hasSessionObjectGuard, onlyAdminsGuard]
  },


]

const router = createRouter({
  history: createWebHistory('/app/'),
  routes,
})

setupNavigationTracking(router)




// Workaround for https://github.com/vitejs/vite/issues/11804
router.onError((err, to) => {
  if (err?.message?.includes?.('Failed to fetch dynamically imported module')) {
    if (!localStorage.getItem('vuetify:dynamic-reload')) {
      console.log('Reloading page to fix dynamic import error')
      localStorage.setItem('vuetify:dynamic-reload', 'true')
      location.assign(to.fullPath)
    } else {
      console.error('Dynamic import error, reloading page did not fix it', err)
    }
  } else {
    console.error(err)
  }
})

router.isReady().then(() => {
  localStorage.removeItem('vuetify:dynamic-reload')
})

export default router
